import { ColDef, RowClassParams, RowStyle } from 'ag-grid-community';
import {
    AgGridReact,
    AgGridReactProps,
    CustomCellRendererProps,
} from 'ag-grid-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWindowSize } from '../../../hooks/useWindowSize';
import { MDBIcon } from 'mdb-react-ui-kit';
import { NavLink } from 'react-router-dom';
import { OpGroupTableDTO } from '@scm/common-legacy';

type TRow = {
    id: string;
    groupId: number;
    location: {
        id: number;
        name: string;
    };
    machine: {
        id: number;
        name: string;
    };
    boxes: {
        qnty: number;
        lowQuantity: boolean;
        empty: boolean;
    }[];
};

export function GroupTableView({
    groupId,
    table,
}: {
    groupId: number;
    table: OpGroupTableDTO;
}) {
    const { t } = useTranslation();

    const boxColumnsCount = useMemo(() => {
        const machineBoxes = table.locations.flatMap((location) =>
            location.machines.flatMap((machine) => machine.boxes.length),
        );
        return machineBoxes.length == 0 ? 0 : Math.max(...machineBoxes);
    }, [table.locations]);

    const { innerWidth } = useWindowSize();
    const isMobile = useMemo(() => innerWidth < 600, []);

    const columnDefs = useMemo<AgGridReactProps<TRow>['columnDefs']>(
        () => [
            {
                headerName: t('location'),
                field: 'location',
                pinned: isMobile ? undefined : 'left',
                cellRenderer: LocationCellRenderer,
            },
            {
                headerName: t('machine'),
                field: 'machine',
                pinned: isMobile ? undefined : 'left',
                cellRenderer: MachineCellRenderer,
            },
            ...Array.from({ length: boxColumnsCount }).map(
                (_, idx) =>
                    ({
                        headerName: `${t('boxes')} (#${idx + 1})`,
                        width: 100,
                        minWidth: 100,
                        valueGetter: (params) =>
                            params.data?.boxes[idx] ?? null,
                        cellRenderer: BoxCellRenderer,
                        flex: idx == boxColumnsCount - 1 ? 1 : undefined,
                    }) as ColDef<TRow>,
            ),
        ],
        [boxColumnsCount],
    );

    const rowData = useMemo<AgGridReactProps<TRow>['rowData']>(() => {
        const rows: TRow[] = [];
        table.locations.forEach((location) => {
            location.machines.forEach((machine) => {
                rows.push({
                    id: `${groupId}-${location.id}-${machine.id}`,
                    groupId,
                    location: {
                        id: location.id,
                        name: location.dto.Name,
                    },
                    machine: {
                        id: machine.id,
                        name: machine.dto.name,
                    },
                    boxes: machine.boxes.map((box) => ({
                        qnty: box.qnty ?? -1,
                        lowQuantity:
                            (box.qnty ?? -1) <= machine.lowQuantityTarget,
                        empty: box.qnty == 0,
                    })),
                });
            });
        });
        return rows;
    }, [table]);

    const getRowClass = useCallback(
        (params: RowClassParams<TRow>): string | undefined => {
            const someEmpty = params.data?.boxes.some((box) => box.empty);
            if (someEmpty) return 'bg-danger bg-opacity-25';

            const someLowQuantity = params.data?.boxes.some(
                (box) => box.lowQuantity,
            );
            if (someLowQuantity) return 'bg-warning bg-opacity-25';
        },
        [],
    );

    return (
        <div className="w-100" style={{ height: '90%' }}>
            <AgGridReact
                rowData={rowData}
                columnDefs={columnDefs}
                getRowClass={getRowClass}
                suppressMovableColumns
            />
        </div>
    );
}

function LocationCellRenderer(
    params: CustomCellRendererProps<TRow, TRow['location'] | null>,
) {
    if (!params.value || !params.data) return null;

    return (
        <div className="d-flex flex-row align-items-center">
            <MDBIcon icon="location-dot" color="primary" className="me-1" />
            <NavLink
                to={`/group/${params.data.groupId}/locations/${params.data.location.id}`}
            >
                {params.value.name}
            </NavLink>
        </div>
    );
}

function MachineCellRenderer(
    params: CustomCellRendererProps<TRow, TRow['machine'] | null>,
) {
    if (!params.value || !params.data) return null;

    return (
        <div className="d-flex flex-row align-items-center">
            <MDBIcon icon="server" color="primary" className="me-1" />
            <NavLink
                to={`/group/${params.data.groupId}/locations/${params.data.location.id}/machines/${params.data.machine.id}`}
            >
                {params.value.name}
            </NavLink>
        </div>
    );
}

function BoxCellRenderer(
    params: CustomCellRendererProps<TRow, TRow['boxes'][number] | null>,
) {
    const { t } = useTranslation();
    if (!params.value) return null;

    const { qnty, lowQuantity, empty } = params.value;

    return (
        <span
            className="d-flex flex-row align-items-center"
            title={
                empty
                    ? t('status:BOX_EMPTY')
                    : lowQuantity
                      ? t('status:BOX_LOW_QUANTITY')
                      : undefined
            }
        >
            {empty && <MDBIcon icon="shopping-cart me-1" color="danger" />}
            {!empty && lowQuantity && (
                <MDBIcon icon="shopping-cart me-1" color="warning" />
            )}
            {qnty}
        </span>
    );
}
